/**
 * Font loading for the HANA widget.
 *
 * Loads the Outfit font via @font-face declarations injected
 * into Shadow DOM. Because Shadow DOM isolates styles, fonts
 * declared only in the shadow root may not be available to the
 * rendering engine. To solve this, we inject the @font-face
 * rules into BOTH the shadow root and the document head.
 *
 * Falls back to the system font stack if the Google Fonts CDN
 * is unreachable (the font-family stack in entity configs
 * already includes system font fallbacks).
 */

/** Google Fonts CDN base URL for the Outfit font */
const OUTFIT_CDN_URL =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap";

/**
 * @font-face CSS for Outfit, loaded from Google Fonts.
 * Uses font-display: swap so text remains visible during load.
 */
function getOutfitFontFaceCSS(): string {
  return /* css */ `
    @import url('${OUTFIT_CDN_URL}');
  `;
}

/** Track whether the document-level font has been injected */
let documentFontInjected = false;

/**
 * Inject the Outfit font into a Shadow DOM root.
 *
 * This creates a <style> element with the @font-face import
 * and appends it to the shadow root. It also injects the same
 * rules into document.head (once) so the browser's font
 * matching system can resolve the font family.
 *
 * @param shadowRoot - The shadow root to inject into
 * @returns The created <style> element (for testing/cleanup)
 */
export function injectOutfitFont(shadowRoot: ShadowRoot): HTMLStyleElement {
  // Inject into the shadow root
  const shadowStyle = document.createElement("style");
  shadowStyle.setAttribute("data-hana-fonts", "outfit");
  shadowStyle.textContent = getOutfitFontFaceCSS();
  shadowRoot.prepend(shadowStyle);

  // Also inject into document head (once) for font matching
  if (!documentFontInjected && typeof document !== "undefined") {
    const existingFontStyle = document.querySelector(
      'style[data-hana-fonts="outfit"]',
    );
    if (!existingFontStyle) {
      const docStyle = document.createElement("style");
      docStyle.setAttribute("data-hana-fonts", "outfit");
      docStyle.textContent = getOutfitFontFaceCSS();
      document.head.appendChild(docStyle);
    }
    documentFontInjected = true;
  }

  return shadowStyle;
}

/**
 * Check whether the entity's font family requires loading Outfit.
 *
 * @param fontFamily - The font-family string from entity branding
 * @returns true if the font stack includes Outfit
 */
export function requiresOutfitFont(fontFamily: string): boolean {
  return fontFamily.toLowerCase().includes("outfit");
}

/**
 * Reset the document font injection tracking.
 * Used in tests to ensure clean state between test runs.
 */
export function _resetFontInjectionState(): void {
  documentFontInjected = false;
}
